import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { ErrorBoundaryWithLogging } from '@/core/ErrorBoundary.atom';
import { ErrorFallbackIcon } from '@/core/ErrorFallback.organism';
import { FluxContext } from '@/core/flux.context';
import { flux } from '@/core/flux.module';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import { apiQueryClient } from '@/main/apiSdk';
import { ApiProvider } from '@seeqdev/api';

/**
 * This component is the highest component of a branch of React components and is an ideal location to provide
 * contexts that are in use throughout the whole application. Detached React roots (e.g. CKEditor-rendered
 * Organizer content) render their own <Root>, so contexts here — including the shared react-query client —
 * must come from module-level singletons to stay consistent across trees.
 */
export const Root = ({ children }: { children?: React.ReactNode }) => {
  return (
    // Wrap in Suspense to handle lazy-loaded components
    <React.Suspense fallback={<LoadingFallback />}>
      {/* No axiosInstance prop: ApiProvider falls back to the globally-registered instance
          (the real one in the app, the mock-adapter one in jest) */}
      <ApiProvider queryClient={apiQueryClient}>
        <DndProvider backend={HTML5Backend}>
          <FluxContext.Provider value={flux}>
            <ErrorBoundaryWithLogging fallback={(error, reset) => <ErrorFallbackIcon error={error} retry={reset} />}>
              {children}
            </ErrorBoundaryWithLogging>
          </FluxContext.Provider>
        </DndProvider>
      </ApiProvider>
    </React.Suspense>
  );
};
